import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const LOGO_TEXT_URL = "https://customer-assets.emergentagent.com/job_461f173b-2f7b-4524-a861-72bba2ed6414/artifacts/xt7j8yp5_SOUNDMOO%20OFFICIAL%20LOGO.png";
const MOO_ICON_URL = "https://customer-assets.emergentagent.com/job_461f173b-2f7b-4524-a861-72bba2ed6414/artifacts/1nwnksnx_Minimalist%20_moo_%20logo%20design.png";

const navLinks = [
  { name: 'Why Soundmoo', href: '#why-soundmoo' },
  { name: 'Projects', href: '#characters' },
  { name: 'Music', href: '#sound-identity' },
  { name: 'Partnerships', href: '#partnerships' },
  { name: 'Contact', href: '#contact' },
];

// Wobble animation variants
const wobbleVariants = {
  idle: { rotate: 0 },
  wobble: {
    rotate: [0, -2, 2, -1, 1, 0],
    transition: {
      duration: 0.5,
      ease: "easeInOut"
    }
  }
};

export const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isWobbling, setIsWobbling] = useState(false);
  const [logoVisible, setLogoVisible] = useState(false);

  // Wobble every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIsWobbling(true);
      setTimeout(() => setIsWobbling(false), 500);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fade logo in/out based on scroll
  useEffect(() => {
    const handleScroll = () => {
      setLogoVisible(window.scrollY > 200);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      // For contact section, scroll to show form with "sea" effect on navbar
      if (href === '#contact') {
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const offset = 80; // Scroll position adjusted for navbar "sea" effect
        window.scrollTo({
          top: absoluteElementTop - offset,
          behavior: 'smooth'
        });
      } else if (href === '#characters') {
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const offset = 30;
        window.scrollTo({
          top: absoluteElementTop - offset,
          behavior: 'smooth'
        });
      } else if (href === '#sound-identity') {
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const offset = 50;
        window.scrollTo({
          top: absoluteElementTop - offset,
          behavior: 'smooth'
        });
      } else {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="fixed top-0 left-0 right-0 z-50"
      >
        <div
          className="absolute inset-0 bg-transparent"
        />
        <div className="soundmoo-container relative">
          <div className="flex items-center justify-between h-20">
            {/* Logo - Text only with wobble animation */}
            <motion.a
              href="#"
              data-testid="navbar-logo"
              className="flex items-center"
              variants={wobbleVariants}
              animate={isWobbling ? "wobble" : "idle"}
              whileHover={{ scale: 1.05 }}
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              style={{ transformOrigin: 'center center' }}
            >
              <img src={LOGO_TEXT_URL} alt="Soundmoo" className="h-24 md:h-32 transition-opacity duration-500" style={{ opacity: logoVisible ? 1 : 0 }} />
            </motion.a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <motion.button
                  key={link.name}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => scrollToSection(link.href)}
                  className="text-white/80 hover:text-white transition-colors text-sm font-medium"
                  whileHover={{ y: -2 }}
                >
                  {link.name}
                </motion.button>
              ))}
              <motion.button
                data-testid="nav-cta-button"
                onClick={() => scrollToSection('#contact')}
                className="btn-primary text-sm py-3 px-6"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get in Touch
              </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              data-testid="mobile-menu-toggle"
              className="md:hidden text-white p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              whileTap={{ scale: 0.95 }}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 pt-24"
            style={{ backgroundColor: 'rgba(10, 26, 63, 0.98)' }}
          >
            <div className="flex flex-col items-center gap-6 p-8">
              {navLinks.map((link, index) => (
                <motion.button
                  key={link.name}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => scrollToSection(link.href)}
                  className="text-white text-xl font-medium"
                >
                  {link.name}
                </motion.button>
              ))}
              <motion.button
                data-testid="mobile-nav-cta"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: navLinks.length * 0.1 }}
                onClick={() => scrollToSection('#contact')}
                className="btn-primary mt-4"
              >
                Get in Touch
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
